<template>
  <header
    class="cal-header"
    :style="`padding-top: ${SafeAreaView.top + 12}px`"
  >
    <button
      type="button"
      class="cal-header__back"
      @click="goBack"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
      ><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
    </button>
    <label class="cal-header__search">
      <input
        v-model="search"
        type="text"
        :placeholder="t('calendarView.search')"
      >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
      >
        <circle
          cx="11"
          cy="11"
          r="7"
        />
        <path d="M20 20l-3.5-3.5" />
      </svg>
    </label>
  </header>

  <SpinerBase
    v-if="isLoading"
    class="spiner"
  />
  <div
    v-else
    class="cal-page"
  >
    <div class="calendar">
      <div
        v-if="viewMode === 'month'"
        class="calendar__title"
      >
        {{ headerTitle }}
      </div>

      <div class="calendar__body">
        <div
          class="calendar__arrow"
          @click="prev"
        >
          ‹
        </div>

        <div class="calendar__grid">
          <div class="calendar__weekdays">
            <div
              v-for="(item, index) in weekDayLabels"
              :key="item"
              class="calendar__weekday"
              :class="{ 'calendar__weekday--active': index === selectedWeekdayIndex }"
            >
              {{ item }}
            </div>
          </div>

          <div class="calendar__days">
            <div
              v-for="day in visibleDays"
              :key="day.dayId"
              class="calendar__day"
              :class="{
                hidden: day.otherMonth,
                active: isSameDay(day.date, eventsStore.selectedDate),
                weekend: day.isWeekend,
              }"
              @click="eventsStore.selectedDate = day.date"
            >
              <span class="calendar__num">{{ day.date.getDate() }}</span>
              <div
                v-if="dayHasEvents(day.date)"
                class="calendar__dots"
              >
                <span
                  v-for="n in Math.min(dayEventsCount(day.date), 2)"
                  :key="n"
                  class="calendar__dot"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          class="calendar__arrow"
          @click="next"
        >
          ›
        </div>
      </div>
    </div>

    <div class="content">
      <div class="tabs">
        <button
          v-for="option in viewOptions"
          :key="option.value"
          type="button"
          class="tabs__item"
          :class="{ 'tabs__item--active': viewMode === option.value }"
          @click="viewMode = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="agenda">
        <template
          v-for="group in agenda"
          :key="group.key"
        >
          <div class="agenda__label">
            {{ group.label }}
          </div>
          <div
            v-for="item in group.items"
            :key="item.id"
            class="agenda__item"
            @click="openDetails(item)"
          >
            <div class="agenda__row">
              <AvatarBase
                :path="petPhoto(item.pet_id)"
                size="58px"
                class="agenda__avatar"
              />
              <div class="agenda__info">
                <div class="agenda__title">
                  {{ item.title }}
                </div>
                <div class="agenda__text">
                  {{ item.description || animalsById[item.pet_id]?.payload?.name }}
                </div>
                <div class="agenda__time">
                  {{ item.all_day ? t('calendarView.allDay') : formatTime(item.date) }}
                </div>
              </div>
              <button
                type="button"
                class="agenda__more"
                @click.stop="openDetails(item)"
              >
                <span /><span /><span />
              </button>
            </div>

            <div
              v-if="isToday(item.date)"
              class="agenda__actions"
            >
              <button
                type="button"
                class="agenda__btn"
                @click.stop="router.push(`/events/${item.id}/edit`)"
              >
                {{ t('calendarView.reschedule') }}
              </button>
            </div>
          </div>
        </template>

        <div
          v-if="!agenda.length"
          class="agenda__empty"
        >
          {{ search.trim() ? t('common.notResultLong') : t('calendarView.noEvents') }}
        </div>
      </div>
    </div>
  </div>

  <button
    class="fab"
    @click="router.push('/events')"
  >
    +
  </button>

  <EventDetailsPane ref="detailsRef" />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useCalendar } from 'vue-use-calendar'
import { SpinerBase } from '@/UI'
import { useNavigation } from '@/composables/useNavigation'
import { SafeAreaView } from '@/helpers/getSafeArea'
import { AvatarBase } from '@/components/AvatarBase'
import { getEvents, getAnimals } from '@/screens/EventsScreen/api'
import { useEventsStore } from '@/states/eventsStore'
import EventDetailsPane from './components/EventDetailsPane.vue'

const { t } = useI18n()
const { goBack } = useNavigation()
const route = useRoute()
const router = useRouter()
const eventsStore = useEventsStore()

const isLoading = ref(true)
const animalsById = ref({})
const detailsRef = ref()
const search = ref('')

const petFilter = computed(() => route.query.petId ? Number(route.query.petId) : null)

const viewMode = ref('week')
const viewOptions = computed(() => [
  { value: 'week', label: t('calendarView.week') },
  { value: 'month', label: t('calendarView.month') },
])

const weekDayLabels = computed(() => [
  t('days.mon'), t('days.tu'), t('days.wed'), t('days.th'), t('days.fri'), t('days.sat'), t('days.sun'),
])

const { useMonthlyCalendar, useWeeklyCalendar } = useCalendar({ firstDayOfWeek: 1, startOn: new Date() })
const monthly = useMonthlyCalendar({ fullWeeks: true, infinite: true })
const weekly = useWeeklyCalendar({ infinite: true })

const visibleDays = computed(() => viewMode.value === 'month' ? monthly.currentMonth.value.days : weekly.currentWeek.value.days)

const headerTitle = computed(() => {
  const { month, year } = monthly.currentMonth.value
  return capitalize(new Date(year, month, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }).replace(/\s*г\.$/, ''))
})

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

const prev = () => (viewMode.value === 'month' ? monthly.prevMonth() : weekly.prevWeek())
const next = () => (viewMode.value === 'month' ? monthly.nextMonth() : weekly.nextWeek())

const isSameDay = (a, b) => a.toDateString() === b.toDateString()

const selectedWeekdayIndex = computed(() => (eventsStore.selectedDate.getDay() + 6) % 7)

const petPhoto = (petId) => animalsById.value[petId]?.payload?.photos?.[0]?.path || animalsById.value[petId]?.payload?.photo

const isToday = (date) => isSameDay(new Date(date), new Date())

const filteredEvents = computed(() => {
  const query = search.value.trim().toLowerCase()
  const byPet = petFilter.value
    ? eventsStore.events.filter((el) => el.pet_id === petFilter.value)
    : eventsStore.events

  if (!query) {return byPet}
  return byPet.filter((el) => `${el.title || ''} ${el.description || ''} ${animalsById.value[el.pet_id]?.payload?.name || ''}`.toLowerCase().includes(query))
})

const eventsByDay = computed(() => {
  const map = {}
  filteredEvents.value.forEach((el) => {
    const key = new Date(el.date).toDateString()
    if (!map[key]) {map[key] = []}
    map[key].push(el)
  })
  return map
})

const dayEventsCount = (date) => eventsByDay.value[date.toDateString()]?.length || 0
const dayHasEvents = (date) => dayEventsCount(date) > 0

const formatTime = (date) => {
  const d = new Date(date)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const dayLabel = (date) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)

  if (d.getTime() === today.getTime()) {return t('calendarView.today')}
  if (d.getTime() === tomorrow.getTime()) {return t('calendarView.tomorrow')}
  return capitalize(d.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }))
}

// agenda: upcoming 14 days starting from the selected date, only days that have events
const agenda = computed(() => {
  const groups = []
  const start = new Date(eventsStore.selectedDate)
  start.setHours(0, 0, 0, 0)

  // при поиске показываем совпадения по всем датам
  if (search.value.trim()) {
    Object.values(eventsByDay.value)
      .map((items) => items.slice().sort((a, b) => new Date(a.date) - new Date(b.date)))
      .sort((a, b) => new Date(a[0].date) - new Date(b[0].date))
      .forEach((items) => groups.push({ key: new Date(items[0].date).toDateString(), label: dayLabel(items[0].date), items }))
    return groups
  }

  for (let i = 0; i < 14; i++) {
    const date = new Date(start)
    date.setDate(date.getDate() + i)
    const items = (eventsByDay.value[date.toDateString()] || []).slice().sort((a, b) => new Date(a.date) - new Date(b.date))

    if (items.length) {
      groups.push({ key: date.toDateString(), label: dayLabel(date), items })
    }
  }

  return groups
})

const openDetails = (event) => {
  detailsRef.value.openModal({ event, pet: animalsById.value[event.pet_id] })
}

const loadData = async () => {
  const [eventsResponse, animalsResponse] = await Promise.all([getEvents(), getAnimals()])
  eventsStore.events = eventsResponse?.events || []
  animalsById.value = Object.fromEntries((animalsResponse?.animals || []).map((el) => [el.id, el]))
  isLoading.value = false
}

onMounted(() => {
  loadData()
})
</script>

<style scoped lang="scss">
.spiner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.cal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px 20px;
  background: #9ad03b;
  border-radius: 0 0 32px 32px;
  position: relative;
  z-index: 2;

  &__back {
    flex: none;
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 50%;
    background: #fff;
    color: #26384b;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__search {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 48px;
    padding: 0 20px;
    border-radius: 24px;
    background: #fff;
    color: #55627a;

    input {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      font-size: 15px;
      font-family: "NunitoRegular";
      color: #26384b;
    }
  }
}

.cal-page {
  min-height: 100vh;
  background: #f3f4f8;
  padding-bottom: 110px;
}

.calendar {
  margin-top: -32px;
  padding: 44px 8px 18px;
  background: #fff;

  &__title {
    text-align: center;
    margin-bottom: 8px;
    font-family: "NunitoBold";
    font-size: 16px;
    color: #26384b;
  }

  &__body {
    display: flex;
    align-items: center;
  }

  &__grid {
    flex: 1;
    min-width: 0;
  }

  &__arrow {
    flex: none;
    width: 20px;
    text-align: center;
    cursor: pointer;
    font-size: 20px;
    color: #55627a;
  }

  &__weekdays,
  &__days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  &__weekday {
    text-align: center;
    color: #55627a;
    font-size: 11px;
    font-family: "NunitoBold";
    padding-bottom: 6px;

    &--active {
      color: #ff5a1f;
    }
  }

  &__day {
    position: relative;
    height: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    color: #26384b;

    &.hidden {
      visibility: hidden;
      pointer-events: none;
    }

    &.weekend {
      color: #6b7090;
    }

    &.active .calendar__num {
      background: #ff5a1f;
      color: #fff;
    }
  }

  &__num {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__dots {
    position: absolute;
    bottom: -2px;
    display: flex;
    gap: 3px;
  }

  &__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #ff5a1f;
  }
}

.content {
  padding: 16px 20px 0;
}

.tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 44px;
  border-radius: 22px;
  background: #fff;
  overflow: hidden;

  &__item {
    border: none;
    background: transparent;
    font-size: 15px;
    font-family: "NunitoBold";
    color: #26384b;

    &--active {
      background: #2a4a4f;
      color: #fff;
      border-radius: 22px;
    }
  }
}

.agenda {
  margin-top: 18px;
  display: grid;
  gap: 10px;

  &__label {
    margin-top: 6px;
    padding-left: 6px;
    font-size: 14px;
    color: #55627a;

    &:first-child {
      margin-top: 0;
    }
  }

  &__item {
    padding: 10px;
    border-radius: 24px;
    background: #fff;
    cursor: pointer;
  }

  &__row {
    display: grid;
    grid-template-columns: 58px 1fr auto;
    gap: 14px;
    align-items: start;
  }

  &__avatar {
    border-radius: 16px;
    overflow: hidden;
  }

  &__info {
    min-width: 0;
  }

  &__title {
    font-size: 15px;
    font-family: "NunitoBold";
    color: #26384b;
  }

  &__text {
    margin-top: 4px;
    font-size: 14px;
    color: #55627a;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__time {
    margin-top: 4px;
    font-size: 12px;
    font-family: "NunitoBold";
    color: #8f98ab;
  }

  &__more {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 6px;
    border: none;
    background: transparent;

    span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: #26384b;
    }
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 12px;
    padding-left: 72px;
  }

  &__btn {
    height: 40px;
    padding: 0 22px;
    border: none;
    border-radius: 20px;
    background: #e9e9fb;
    color: #26384b;
    font-size: 14px;
    font-family: "NunitoBold";
  }

  &__empty {
    text-align: center;
    color: #6b7090;
    padding: 20px;
  }
}

.fab {
  position: absolute;
  right: 20px;
  bottom: 30px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #9ad03b;
  color: #26384b;
  font-size: 30px;
  line-height: 1;
  box-shadow: 0px 5px 20px rgba(154, 208, 59, 0.5);
  z-index: 20;
}

[theme="dark"] {
  .cal-header__back {
    background: #37363c;
    color: #fff;
  }

  .cal-page {
    background: #1b1b1b;
  }

  .calendar {
    background: #262626;
  }

  .calendar__title,
  .calendar__day {
    color: #fff;
  }

  .calendar__day.weekend {
    color: #9a9fc0;
  }

  .calendar__weekday,
  .calendar__arrow,
  .agenda__label {
    color: #9a9fc0;
  }

  .calendar__weekday--active {
    color: #ff5a1f;
  }

  .agenda__item,
  .tabs,
  .cal-header__search {
    background: #37363c;
  }

  .agenda__title,
  .tabs__item,
  .cal-header__search input {
    color: #fff;
  }

  .agenda__text {
    color: #b0b2c2;
  }

  .agenda__more span {
    background: #fff;
  }

  .agenda__btn {
    background: #4a4970;
    color: #fff;
  }
}
</style>
